import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { orderBy } from "lodash-es";
import { Heading } from "react-aria-components";
import z from "zod";

import { Button } from "@/components/base/button";
import { useAppForm } from "@/components/base/form";
import { Modal } from "@/components/base/modal";
import { title } from "@/components/base/primitives";
import { getPoolStats, type PoolTeamStats } from "@/hooks/matches";
import { useTRPC } from "@/integrations/trpc/react";
import { isNotNullOrUndefined } from "@/utils/types";

import { TeamRadioList } from "./team-radio-list";

export type SwitchTeamFormProps = {
  tournamentDivisionId: number;
  matchId: number;
  slot: "teamAId" | "teamBId";
  currentTeamId: number | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

const teamIdSchema = z.object({
  teamId: z.number(),
});

export function SwitchTeamForm({
  tournamentDivisionId,
  matchId,
  slot,
  currentTeamId,
  onOpenChange,
  ...props
}: SwitchTeamFormProps) {
  const queryClient = useQueryClient();
  const trpc = useTRPC();

  const teamsOptions = trpc.tournaments.getTeams.queryOptions({
    tournamentDivisionId,
  });
  const poolsOptions = trpc.tournaments.getPools.queryOptions({
    tournamentDivisionId,
  });

  const { mutate, failureReason } = useMutation({
    ...trpc.tournaments.switchTeam.mutationOptions(),
    onSuccess: () => {
      queryClient.invalidateQueries(trpc.tournaments.pathFilter());
      onOpenChange(false);
    },
  });

  const form = useAppForm({
    defaultValues: {
      teamId: null as unknown as number,
    },
    validators: {
      onMount: teamIdSchema,
      onChange: teamIdSchema,
    },
    onSubmit: ({ value: { teamId } }) => {
      mutate({
        matchId,
        slot,
        teamId,
      });
    },
  });

  const { data: teams } = useQuery({
    ...teamsOptions,
    select: (data) =>
      data.filter(
        (team) =>
          team.id !== currentTeamId &&
          team.status === "confirmed" &&
          team.poolTeam?.finish,
      ),
  });

  const { data: stats } = useQuery({
    ...poolsOptions,
    select: (data) => {
      const stats = new Map(
        data
          .map((pool) => {
            const stats = getPoolStats(pool);

            return stats ? { pool: pool.name, stats } : null;
          })
          .filter(isNotNullOrUndefined)
          .flatMap(({ stats }) =>
            Object.keys(stats).map(
              (teamId) =>
                [
                  Number.parseInt(teamId, 10),
                  stats[teamId as unknown as number],
                ] as [number, PoolTeamStats],
            ),
          ),
      );

      return stats;
    },
  });

  const orderedTeams = orderBy(
    teams,
    [
      (team) => team.poolTeam?.finish,
      (team) => {
        const teamStats = stats?.get(team.id);

        if (teamStats) {
          return (
            teamStats.wins.size / (teamStats.wins.size + teamStats.losses.size)
          );
        }

        return 0;
      },
      (team) => stats?.get(team.id)?.totalPointDiff,
      (team) => team.poolTeam?.pool.name,
    ],
    ["asc", "desc", "desc", "asc"],
  );

  return (
    <Modal {...props} onOpenChange={onOpenChange}>
      <div className="relative flex flex-col space-y-4 p-3">
        <Heading className={title({ size: "sm" })} slot="title">
          Switch Team
        </Heading>

        <p className="mb-6 text-sm text-gray-700">
          Select a team to place in this bracket slot. If the selected team is
          already in another slot, it will be removed from that slot.
        </p>

        <form
          className="flex flex-col space-y-6"
          onSubmit={(e) => {
            e.preventDefault();

            form.handleSubmit();
          }}
        >
          {failureReason && (
            <form.AppForm>
              <form.Alert
                title={"Unable to switch team"}
                description={failureReason.message}
              />
            </form.AppForm>
          )}

          <form.AppField
            name="teamId"
            children={(field) => (
              <TeamRadioList
                teams={orderedTeams}
                stats={stats}
                onChange={(teamId) => field.handleChange(teamId)}
              />
            )}
          />

          <form.AppForm>
            <form.Footer>
              <Button onPress={() => onOpenChange(false)}>Cancel</Button>

              <form.SubmitButton>Switch Team</form.SubmitButton>
            </form.Footer>
          </form.AppForm>
        </form>
      </div>
    </Modal>
  );
}
